import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DashboardService } from '@app/core/services/dashboard.service';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { AdvancedIntervalPickerComponent } from '@app/advanced-interval-picker/advanced-interval-picker.component';
import { QuickRange } from '@app/shared/types/IntervalPicker.types';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule, 
    FormsModule,
    HttpClientModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatDividerModule,
    MatAutocompleteModule,
    AdvancedIntervalPickerComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    provideNativeDateAdapter()
  ]
})
export class DashboardComponent implements OnInit {
  ustanove: any[] = [];
  laboratorije: any[] = [];
  
  dateFrom: Date = this.getDefaultStart();
  dateTo: Date = this.getDefaultEnd();
  selectedUstanova: any = null;
  selectedLaboratorija: any = null;
  grafanaHtml: SafeHtml = '';
  ustanoveFiltered: any[] = [];
  searchUstanovaText: string = '';
  searchLaboratorijaText: string = '';
  laboratorijeFiltered: any[] = [];

  constructor(
    private dashboardService: DashboardService, 
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    this.updateGrafana();
    this.loadInstitutions();
  }

  myDashboardRanges: QuickRange[] = [
    {
      label: 'Danas',
      calculate: () => {
        const from = new Date();
        from.setHours(0, 0, 0, 0);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Juče',
      calculate: () => {
        const from = new Date();
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        const to = new Date();
        to.setDate(to.getDate() - 1);
        to.setHours(23, 59, 59, 999);
        return { from, to };
      }
    },
    {
      label: 'Poslednja 2 dana',
      calculate: () => {
        const from = new Date();
        from.setDate(from.getDate() - 2);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Poslednjih 7 dana',
      calculate: () => {
        const from = new Date();
        from.setDate(from.getDate() - 7);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Poslednjih 30 dana',
      calculate: () => {
        const from = new Date();
        from.setDate(from.getDate() - 30);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Ovaj mesec',
      calculate: () => {
        const from = new Date();
        from.setDate(1); 
        from.setHours(0, 0, 0, 0);
        const to = new Date();
        to.setMonth(to.getMonth() + 1, 0); 
        to.setHours(23, 59, 59, 999);
        return { from, to };
      }
    },
    {
      label: 'Prethodni mesec',
      calculate: () => {
        const from = new Date();
        from.setMonth(from.getMonth() - 1, 1); 
        from.setHours(0, 0, 0, 0);
        const to = new Date();
        to.setDate(0); 
        to.setHours(23, 59, 59, 999);
        return { from, to };
      }
    },
    {
      label: 'Poslednjih 6 meseci',
      calculate: () => {
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Poslednja 1 godina',
      calculate: () => {
        const from = new Date();
        from.setFullYear(from.getFullYear() - 1);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Poslednje 2 godine',
      calculate: () => {
        const from = new Date();
        from.setFullYear(from.getFullYear() - 2);
        const to = new Date();
        return { from, to };
      }
    },
    {
      label: 'Poslednjih 5 godina',
      calculate: () => {
        const from = new Date();
        from.setFullYear(from.getFullYear() - 5);
        const to = new Date();
        return { from, to };
      }
    }
  ];

  displayFn(item: any): string {
    return item && item.text ? item.text : (typeof item === 'string' ? item : '');
  }

  onIntervalUpdated(event: { from: Date; to: Date }) {
    this.dateFrom = event.from;
    this.dateTo = event.to;
    this.onDateChange();
  }

  getDefaultStart(): Date {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  getDefaultEnd(): Date {
    const date = new Date();
    return date;
  }

  clearAllFilters() {
    this.dateFrom = this.getDefaultStart();
    this.dateTo = this.getDefaultEnd();
    this.resetUstanovaAndLaboratorija();
    this.loadInstitutions();
    this.updateGrafana();
  }

  onDateChange() {
    if (this.dateFrom && this.dateTo) {
      this.resetUstanovaAndLaboratorija();
      this.dashboardService.getInstitutions(this.dateFrom, this.dateTo).subscribe({
        next: (data) => {
          const institutions = this.normalizeListResponse(data);
          this.ustanove = institutions;
          this.ustanoveFiltered = [...institutions];
          this.updateGrafana();
        },
        error: (err) => console.error('Greška Ustanove', err)
      });
    }
  }

  otvoriUstanove() {
    if (!this.searchUstanovaText) {
      this.ustanoveFiltered = [...this.ustanove];
    }
  }

  filtrirajUstanove(event: any) {
    const query = typeof event === 'string' ? event : event.target.value;
    this.searchUstanovaText = query;
    const lowerQuery = (query || '').toLowerCase().trim();

    if (!lowerQuery) {
      this.ustanoveFiltered = [...this.ustanove];
      this.resetUstanovaAndLaboratorija();
      this.updateGrafana();
      return;
    }

    this.ustanoveFiltered = this.ustanove.filter(u =>
      u.text && u.text.toLowerCase().includes(lowerQuery)
    );
  }

  onUstanovaSelected(ustanova: any) {
    if (!ustanova) return;

    this.selectedUstanova = ustanova.value;
    this.searchUstanovaText = ustanova.text;

    this.clearLaboratorija();

    if (this.selectedUstanova && this.dateFrom && this.dateTo) {
      this.dashboardService.getLaboratories(this.dateFrom, this.dateTo, this.selectedUstanova).subscribe({
        next: (data) => {
          const laboratories = this.normalizeListResponse(data);
          this.laboratorije = laboratories;
          this.laboratorijeFiltered = [...laboratories];
          this.updateGrafana();
        },
        error: (err) => console.error('Greška Laboratorije', err)
      });
    }
  }

  otvoriLaboratorije() {
    if (!this.searchLaboratorijaText) {
      this.laboratorijeFiltered = [...this.laboratorije];
    }
  }

  filtrirajLaboratorije(event: Event) {
    const query = (event.target as HTMLInputElement).value;
    this.searchLaboratorijaText = query;
    const lowerQuery = (query || '').toLowerCase().trim();

    if (!lowerQuery) {
      this.laboratorijeFiltered = [...this.laboratorije];
      this.selectedLaboratorija = null;
      this.updateGrafana();
      return;
    }

    this.laboratorijeFiltered = this.laboratorije.filter(l => 
      l.text && l.text.toLowerCase().includes(lowerQuery)
    );
  }

  onLaboratorijaSelected(laboratorija: any) {
    if (!laboratorija) return;
    
    this.selectedLaboratorija = laboratorija.value;
    this.searchLaboratorijaText = laboratorija.text;
    this.updateGrafana();
  }

  onLaboratorijaChange() {
    this.updateGrafana();
  }

  private normalizeListResponse(data: unknown): any[] {
    if (Array.isArray(data)) {
      return data.filter(item => item && item.text !== null && item.text !== undefined && String(item.text).trim() !== '');
    }
    return [];
  }

  updateGrafana() {
    const fullUrl = this.dashboardService.buildGrafanaUrl(
      this.dateFrom,
      this.dateTo,
      this.selectedUstanova, 
      this.selectedLaboratorija
    );

    const iframe = `<iframe 
      src="${fullUrl}" 
      width="100%" 
      height="1300" 
      frameborder="0" 
      key="${new Date().getTime()}">
    </iframe>`;

    this.grafanaHtml = this.sanitizer.bypassSecurityTrustHtml(iframe);
  }

  private clearLaboratorija() {
    this.selectedLaboratorija = null;
    this.searchLaboratorijaText = '';
    this.laboratorije = [];
    this.laboratorijeFiltered = [];
  }

  private resetUstanovaAndLaboratorija() {
    this.selectedUstanova = null;
    this.searchUstanovaText = '';
    this.clearLaboratorija();
  }

  private loadInstitutions() {
    if (!this.dateFrom || !this.dateTo) return;

    this.dashboardService.getInstitutions(this.dateFrom, this.dateTo).subscribe({
      next: (data) => {
        const institutions = this.normalizeListResponse(data);
        this.ustanove = institutions;
        this.ustanoveFiltered = [...institutions];
      },
      error: (err) => console.error('Greška Ustanove', err)
    });
  }
}